export type GestureEventPinch = CustomEvent<{ scale: number; centerX: number; centerY: number }>
export type GestureEventDrag = CustomEvent<{
  deltaX: number
  deltaY: number
  clientX: number
  clientY: number
  end: boolean
}>
export type GestureEventDoubleTap = CustomEvent<{ x: number; y: number }>

export class GestureHelper {
  private __pointers = new Map<number, { x: number; y: number }>()
  private __lastTapTime = 0
  private __lastPinchDistance = 0
  private __dragStart: { x: number; y: number } | null = null
  private element: Element

  constructor(element: Element) {
    this.element = element
    this.init()
  }

  private init() {
    this.element.addEventListener('pointerdown', this.handlePointerDown as EventListener)
  }

  dispose() {
    this.element.removeEventListener('pointerdown', this.handlePointerDown as EventListener)
    this.removeGlobalListeners()
  }

  private handlePointerDown = (e: PointerEvent) => {
    e.preventDefault()
    const point = { x: e.clientX, y: e.clientY }
    this.__pointers.set(e.pointerId, point)

    this.addGlobalListeners()
  }

  private handlePointerMove = (e: PointerEvent) => {
    e.preventDefault()
    const point = { x: e.clientX, y: e.clientY }
    this.__pointers.set(e.pointerId, point)

    if (this.__pointers.size === 2) {
      this.handlePinch()
    } else if (this.__pointers.size === 1) {
      this.handleDrag(point, false)
    }
  }

  private handlePointerUp = (e: PointerEvent) => {
    const point = { x: e.clientX, y: e.clientY }
    this.__pointers.delete(e.pointerId)

    if (this.__dragStart) {
      this.handleDrag(point, true)
    }

    if (this.__pointers.size === 0) {
      this.removeGlobalListeners()
      this.__dragStart = null
      this.__lastPinchDistance = 0
      this.handleTap(point)
    }
  }

  private handlePinch() {
    const pointers = Array.from(this.__pointers.values()) as { x: number; y: number }[]
    const currentDistance = this.getDistance(pointers[0], pointers[1])

    if (this.__lastPinchDistance) {
      const scale = currentDistance / this.__lastPinchDistance
      const center = {
        x: (pointers[0].x + pointers[1].x) / 2,
        y: (pointers[0].y + pointers[1].y) / 2
      }

      this.element.dispatchEvent(
        new CustomEvent('pinch', {
          detail: { scale, centerX: center.x, centerY: center.y }
        })
      )
    }

    this.__lastPinchDistance = currentDistance
  }

  private handleDrag(point: { x: number; y: number }, end: boolean) {
    if (this.__dragStart) {
      const deltaX = point.x - this.__dragStart.x
      const deltaY = point.y - this.__dragStart.y

      this.element.dispatchEvent(
        new CustomEvent('drag', {
          detail: { deltaX, deltaY, clientX: point.x, clientY: point.y, end }
        })
      )

      if (end) {
        this.__dragStart = null
      } else {
        this.__dragStart = point
      }
    } else if (!end && this.__pointers.size === 1) {
      /* dragging을 시작하려면, pointer 개수가 1개인 것을 (지연)확인해야한다. */
      setTimeout(() => {
        if (this.__pointers.size === 1 && !this.__dragStart) {
          this.__dragStart = point
        }
      }, 30)
    }
  }

  private handleTap(point: { x: number; y: number }) {
    const currentTime = performance.now()
    const timeSinceLastTap = currentTime - this.__lastTapTime

    if (timeSinceLastTap < 300) {
      this.element.dispatchEvent(
        new CustomEvent('doubletap', {
          detail: { x: point.x, y: point.y }
        })
      )
    }

    this.__lastTapTime = currentTime
  }

  private getDistance(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
    const dx = p1.x - p2.x
    const dy = p1.y - p2.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  private addGlobalListeners() {
    document.addEventListener('pointermove', this.handlePointerMove as EventListener)
    document.addEventListener('pointerup', this.handlePointerUp as EventListener)
    document.addEventListener('pointercancel', this.handlePointerUp as EventListener)
  }

  private removeGlobalListeners() {
    document.removeEventListener('pointermove', this.handlePointerMove as EventListener)
    document.removeEventListener('pointerup', this.handlePointerUp as EventListener)
    document.removeEventListener('pointercancel', this.handlePointerUp as EventListener)
  }
}
